/**
 * Rage-click / dead-click detection, live in the browser.
 *
 * This is a deliberate port of the server-side detectors in
 * `trace-compiler/src/detectors.ts` (humanbehavior-v2). Both must answer
 * "was this click dead?" the same way, because the raw `$rageclick` /
 * `$deadclick` events this file emits feed the dashboard Health Signals, the
 * issues dashboard, the replay inspector, the visitor timeline and
 * `hb_search_replays`, while the compiled `dead_click` / `rage_click` trace
 * rows feed Issues and `hb_find_friction`. When the two disagree, the product
 * contradicts itself about the same session.
 *
 * Thresholds live in FRICTION below and are asserted against the
 * trace-compiler constants by a parity test in the monorepo
 * (`tests/trace-compiler/sdk-friction-parity.test.ts`). Change one, change both.
 *
 * Two divergences from trace-compiler remain, and are not fixable from either
 * side alone:
 *   1. trace-compiler suppresses "ambient" mutations (tickers, clocks, ad
 *      rotation) that fire with no user interaction, so they don't mask a dead
 *      click. Doing that live would mean scoring every MutationRecord on the
 *      main thread of the customer's app; we accept the false negatives.
 *   2. `selectionchange` is deliberately NOT treated as a page reaction. The
 *      old SDK rule used it to suppress text-selection false positives;
 *      trace-compiler has no selection signal at all, and the interactive-only
 *      gate plus the two-gesture rule cover those cases instead.

 * A clickable <div> whose `cursor: pointer` comes from a stylesheet rather than
 * an inline style is invisible to BOTH sides: trace-compiler only has the rrweb
 * snapshot's inline styles, and reading the computed style here would break
 * target resolution (see hasInlinePointerCursor).
 */

// Mirror of the constants in trace-compiler/src/detectors.ts. Declared as plain
// consts, not object properties, so the minifier can inline them — the browser
// bundle sits against a hard size-limit budget.

/** Clicks on one target within RAGE_WINDOW_MS needed to call it rage. */
const RAGE_MIN_CLICKS = 4;
const RAGE_WINDOW_MS = 2_000;
/** How long the page has to react before a click counts as dead. */
const DEAD_CLICK_REACTION_MS = 1_500;
/** One unreacted click is a shrug; the same control ignoring the user twice is broken. */
const DEAD_CLICK_MIN_OCCURRENCES = 2;
/** Clicks closer than this on one target are a single gesture (double/triple-click). */
const DEAD_CLICK_GESTURE_MS = 700;
/** Duplicate capture: two clicks on one target closer than this are one click. */
const MIN_CLICK_SPACING_MS = 30;

/** Re-exported as one object for tests and for the cross-repo parity guard. */
export const FRICTION = {
    RAGE_MIN_CLICKS,
    RAGE_WINDOW_MS,
    DEAD_CLICK_REACTION_MS,
    DEAD_CLICK_MIN_OCCURRENCES,
    DEAD_CLICK_GESTURE_MS,
    MIN_CLICK_SPACING_MS,
} as const;

// Mirror of INTERACTIVE_TAGS / INTERACTIVE_ROLES in trace-compiler/src/vdom.ts.
const INTERACTIVE_TAGS = new Set('button a input select textarea label summary option'.split(' '));
const INTERACTIVE_ROLES = new Set(
    'button link checkbox radio tab menuitem switch combobox option slider textbox'.split(' '),
);

// Form controls where a click legitimately produces no DOM mutation (clicking
// into a text field, toggling focus). Never dead-click OR rage candidates.
const FORM_CONTROL_TAGS = new Set('input select textarea label option'.split(' '));

// Copy-to-clipboard controls: the click's effect is a clipboard write, which
// leaves no DOM trace in many implementations. Silence is expected.
const CLIPBOARD_TARGET = /\bcopy\b/i;

// ARIA states that mark a control as already selected/on, where a click is a
// no-op by design (the selected tab, the pressed toggle, the checked option).
const SELECTED_STATE_ATTRS = ['aria-selected', 'aria-pressed', 'aria-checked'] as const;

// Hop budget when walking from the event target up to the clicked control.
const RESOLVE_MAX_HOPS = 30;

// Bound on retained per-target state, so a long session on a page that mints
// new target keys forever (virtualised lists) cannot grow without limit.
const MAX_TRACKED_TARGETS = 500;

/** What kind of page response a signal represents. */
export type ReactionKind =
    // DOM mutation or navigation. Counts as a reaction for BOTH detectors.
    | 'dom'
    // Scroll, or typing. Counts for dead clicks only — trace-compiler's rage
    // detector reads reactionTs alone, which excludes these.
    | 'soft';

export interface FrictionClickInfo {
    /** The resolved control (the interactive ancestor), not the raw event target. */
    node: Element;
    /** clientX/clientY of the first click in the group. */
    x: number;
    y: number;
    /** Timestamp of the first click in the group. */
    tsMs: number;
    /** Rage: clicks in the burst. Dead: unreacted clicks recorded on this target. */
    clickCount: number;
    /** Dead: distinct gestures (double-clicks collapsed). Undefined for rage. */
    occurrences?: number;
    /** Rage: burst span. Dead: 0. */
    durationMs: number;
}

export interface FrictionClickOptions {
    /** Called when a rage or dead click is confirmed. */
    emit: (kind: 'rage' | 'dead', info: FrictionClickInfo) => void;
    /** Injectable for tests. */
    now?: () => number;
    setTimeout?: (fn: () => void, ms: number) => number;
    clearTimeout?: (id: number) => void;
    /** Injectable for tests; defaults to window.location.href. */
    currentUrl?: () => string;
}

/**
 * Walk up from a (possibly deeply nested) event target to the control the user
 * semantically clicked. `interactive: false` means we found no clickable
 * ancestor — the click landed on plain content.
 */
export function resolveInteractive(target: Element | null): { node: Element; interactive: boolean } | null {
    let cur: Element | null = target;
    let hops = 0;
    let firstElement: Element | null = null;
    while (cur && hops < RESOLVE_MAX_HOPS) {
        if (cur.nodeType === 1 && cur.tagName) {
            if (!firstElement) firstElement = cur;
            const tag = cur.tagName.toLowerCase();
            const role = (cur.getAttribute('role') ?? '').toLowerCase();
            if (
                INTERACTIVE_TAGS.has(tag) ||
                INTERACTIVE_ROLES.has(role) ||
                cur.getAttribute('onclick') !== null ||
                (cur as unknown as { onclick?: unknown }).onclick != null ||
                cur.getAttribute('tabindex') !== null ||
                hasInlinePointerCursor(cur)
            ) {
                return { node: cur, interactive: true };
            }
        }
        cur = cur.parentElement;
        hops++;
    }
    return firstElement ? { node: firstElement, interactive: false } : null;
}

/**
 * Inline `cursor: pointer` — the visual affordance signal a React-style
 * clickable <div> carries when it has no semantic markup.
 *
 * Deliberately NOT getComputedStyle: `cursor` inherits, so every child of a
 * clickable div computes to `pointer` and the walk would stop at whichever
 * span or icon the click happened to land on. That splits one control into
 * several target keys, so its gestures never group. Inline-only is also
 * exactly what trace-compiler can see in the rrweb snapshot.
 */
function hasInlinePointerCursor(el: Element): boolean {
    return /cursor\s*:\s*pointer/i.test(el.getAttribute('style') ?? '');
}

/** True when a click on this element takes effect OUTSIDE the recorded tab. */
export function opensElsewhere(node: Element, currentUrl: string): boolean {
    if ((node.tagName || '').toLowerCase() !== 'a') return false;
    if ((node.getAttribute('target') ?? '').toLowerCase() === '_blank') return true;
    if (node.getAttribute('download') !== null) return true;
    const href = node.getAttribute('href') ?? '';
    if (/^(mailto:|tel:|sms:|blob:|javascript:void)/i.test(href)) return true;
    if (/^https?:\/\//i.test(href)) {
        try {
            return new URL(href).origin !== new URL(currentUrl).origin;
        } catch {
            return false;
        }
    }
    return false;
}

/** True when the control is already selected/on, so a click is a no-op by design. */
export function alreadySelected(node: Element): boolean {
    return SELECTED_STATE_ATTRS.some((attr) => (node.getAttribute(attr) ?? '').toLowerCase() === 'true');
}

/**
 * Stable per-control identity. Mirror of VDom.targetKey in trace-compiler, so a
 * React re-render that swaps the DOM node still groups with its earlier clicks.
 */
export function targetKey(node: Element): string {
    const tag = (node.tagName || 'element').toLowerCase();
    const id = node.getAttribute('id');
    if (id) return `${tag}#${stableIdKey(id)}`;
    const testid = node.getAttribute('data-testid');
    if (testid) return `${tag}[testid=${testid}]`;
    const name = node.getAttribute('name');
    if (name) return `${tag}[name=${name}]`;
    const aria = node.getAttribute('aria-label')?.trim();
    const text = (aria || (node.textContent ?? '').slice(0, 40))
        .toLowerCase()
        .replace(/\d+/g, '*')
        .replace(/\s+/g, ' ')
        .trim();
    if (text) return `${tag}[text=${text.slice(0, 40)}]`;
    const className = node.getAttribute('class') ?? '';
    const cls = className
        .split(/\s+/)
        .filter((c) => c && !/\d/.test(c))
        .slice(0, 2)
        .join('.');
    return cls ? `${tag}.${cls}` : tag;
}

// Generated ids (React useId, CSS-in-JS) differ per render; collapse tokens
// carrying entropy so one control doesn't fingerprint into thousands.
function stableIdKey(id: string): string {
    return id
        .split(/[-_:]/)
        .map((part) => (/\d/.test(part) ? '*' : part))
        .join('-');
}

/**
 * The shared eligibility gate. Returns null when this click can never be rage
 * or dead. Same five checks, in the same order, as both trace-compiler
 * detectors.
 */
export function frictionEligible(
    target: Element | null,
    currentUrl: string,
): { node: Element; key: string } | null {
    if (!target || target.nodeType !== 1 || !target.tagName) return null;
    const resolved = resolveInteractive(target);
    if (!resolved || !resolved.interactive) return null;
    const node = resolved.node;
    if (opensElsewhere(node, currentUrl)) return null;
    if (alreadySelected(node)) return null;
    if (FORM_CONTROL_TAGS.has((node.tagName || '').toLowerCase())) return null;
    return { node, key: targetKey(node) };
}

interface PendingDeadClick {
    node: Element;
    key: string;
    x: number;
    y: number;
    tsMs: number;
    timer: number;
}

interface TargetState {
    /** Timestamps of clicks on this target, pruned to the rage window. */
    burst: number[];
    /** Scheduled rage verdict for the current burst, if any. */
    rageTimer: number | null;
    /** First/last click of the burst under evaluation, and its full size. */
    burstFirstTs: number;
    burstLastTs: number;
    burstCount: number;
    /** Where the burst under evaluation started. */
    burstOrigin: { node: Element; x: number; y: number } | null;
    /** Timestamp of the last click we accepted (30ms duplicate-capture dedupe). */
    lastClickTs: number;
    /** Unreacted-click bookkeeping for the dead-click occurrence rule. */
    unreactedGestures: number;
    unreactedClicks: number;
    lastUnreactedTs: number;
    /** The click that opened the current unreacted streak. */
    firstUnreacted: PendingDeadClick | null;
    /** One dead-click event per target per session, matching one compiled row. */
    deadEmitted: boolean;
}

function newTargetState(): TargetState {
    return {
        burst: [],
        rageTimer: null,
        burstFirstTs: 0,
        burstLastTs: 0,
        burstCount: 0,
        burstOrigin: null,
        lastClickTs: Number.NEGATIVE_INFINITY,
        unreactedGestures: 0,
        unreactedClicks: 0,
        lastUnreactedTs: Number.NEGATIVE_INFINITY,
        firstUnreacted: null,
        deadEmitted: false,
    };
}

/**
 * Live rage/dead click detector. Feed it clicks and page reactions; it calls
 * `emit` when a verdict is reached. Holds no DOM listeners of its own — the
 * tracker owns those.
 */
export class FrictionClickDetector {
    private readonly emit: FrictionClickOptions['emit'];
    private readonly now: () => number;
    private readonly schedule: (fn: () => void, ms: number) => number;
    private readonly unschedule: (id: number) => void;
    private readonly currentUrl: () => string;

    /** Reaction timestamps, sorted, pruned to a bounded recent window. */
    private domReactions: number[] = [];
    private softReactions: number[] = [];

    private targets = new Map<string, TargetState>();
    private pendingDead = new Map<number, PendingDeadClick>();
    private nextPendingId = 1;

    constructor(options: FrictionClickOptions) {
        this.emit = options.emit;
        this.now = options.now ?? (() => Date.now());
        this.schedule = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms) as unknown as number);
        this.unschedule = options.clearTimeout ?? ((id) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>));
        this.currentUrl = options.currentUrl ?? (() => (typeof window !== 'undefined' ? window.location.href : ''));
    }

    /** Record a page response. Call on mutation/navigation ('dom') or scroll/typing ('soft'). */
    onReaction(kind: ReactionKind, tsMs: number = this.now()): void {
        const list = kind === 'dom' ? this.domReactions : this.softReactions;
        list.push(tsMs);
        // Reactions only matter until every click that could see them has
        // settled, so old entries are dead weight. Keep a bounded tail.
        if (list.length > 256) list.splice(0, list.length - 128);
    }

    /** Feed every click. Ineligible targets are dropped here. */
    onClick(target: Element | null, x: number, y: number, tsMs: number = this.now()): void {
        const eligible = frictionEligible(target, this.currentUrl());
        if (!eligible) return;
        const { node, key } = eligible;
        const state = this.stateFor(key);

        // Duplicate capture (double-registered listeners) produces bursts no
        // human can perform. One physical click.
        if (tsMs - state.lastClickTs < MIN_CLICK_SPACING_MS) return;
        state.lastClickTs = tsMs;

        this.trackRage(state, node, key, x, y, tsMs);
        this.trackDead(state, node, key, x, y, tsMs);
    }

    /** Drop all pending verdicts (page unload, session end). */
    reset(): void {
        for (const pending of this.pendingDead.values()) this.unschedule(pending.timer);
        this.pendingDead.clear();
        for (const state of this.targets.values()) {
            if (state.rageTimer !== null) this.unschedule(state.rageTimer);
        }
        this.targets.clear();
        this.domReactions = [];
        this.softReactions = [];
    }

    private stateFor(key: string): TargetState {
        let state = this.targets.get(key);
        if (!state) {
            if (this.targets.size >= MAX_TRACKED_TARGETS) {
                // Evict the oldest insertion; Map preserves insertion order. A
                // rage timer left pointing at it settles into a no-op lookup.
                const oldest = this.targets.keys().next();
                if (!oldest.done) this.targets.delete(oldest.value);
            }
            state = newTargetState();
            this.targets.set(key, state);
        }
        return state;
    }

    // ---- rage ------------------------------------------------------------

    private trackRage(state: TargetState, node: Element, key: string, x: number, y: number, tsMs: number): void {
        // A burst is already awaiting its verdict: greedily absorb this click
        // if the gap is inside the window, so one frustrated flurry is one
        // event even when it runs longer than RAGE_WINDOW_MS.
        if (state.rageTimer !== null) {
            if (tsMs - state.burstLastTs <= RAGE_WINDOW_MS) {
                state.burstCount++;
                state.burstLastTs = tsMs;
                this.unschedule(state.rageTimer);
                state.rageTimer = this.scheduleRageVerdict(key);
            }
            return;
        }

        state.burst.push(tsMs);
        // Sliding window anchored on the burst, exactly as the compiler does.
        while (state.burst.length > 0 && tsMs - state.burst[0] > RAGE_WINDOW_MS) {
            state.burst.shift();
        }
        if (state.burst.length < RAGE_MIN_CLICKS) return;

        state.burstFirstTs = state.burst[0];
        state.burstLastTs = tsMs;
        state.burstCount = state.burst.length;
        state.burstOrigin = { node, x, y };
        // Rage requires the page to have IGNORED the burst, and both a
        // continuation click and a late reaction can still arrive — so the
        // verdict waits out the window plus the reaction grace.
        state.rageTimer = this.scheduleRageVerdict(key);
    }

    private scheduleRageVerdict(key: string): number {
        return this.schedule(
            () => this.settleRage(key),
            RAGE_WINDOW_MS + DEAD_CLICK_REACTION_MS,
        );
    }

    private settleRage(key: string): void {
        const state = this.targets.get(key);
        if (!state || state.rageTimer === null) return;
        state.rageTimer = null;
        const count = state.burstCount;
        const reacted = hasReactionInRange(
            this.domReactions,
            state.burstFirstTs,
            state.burstLastTs + DEAD_CLICK_REACTION_MS,
        );
        if (!reacted && count >= RAGE_MIN_CLICKS && state.burstOrigin) {
            this.emit('rage', {
                node: state.burstOrigin.node,
                x: state.burstOrigin.x,
                y: state.burstOrigin.y,
                tsMs: state.burstFirstTs,
                clickCount: count,
                durationMs: state.burstLastTs - state.burstFirstTs,
            });
        }
        // Emitted or not, this burst is spent: later clicks start a fresh one.
        state.burst = [];
        state.burstCount = 0;
        state.burstOrigin = null;
    }

    // ---- dead ------------------------------------------------------------

    private trackDead(state: TargetState, node: Element, key: string, x: number, y: number, tsMs: number): void {
        if (state.deadEmitted) return;
        // Copy-to-clipboard controls show no DOM feedback by design.
        if (CLIPBOARD_TARGET.test(node.getAttribute('aria-label') ?? '') ||
            CLIPBOARD_TARGET.test((node.textContent ?? '').slice(0, 60))) {
            return;
        }
        const id = this.nextPendingId++;
        const timer = this.schedule(() => this.settleDead(id), DEAD_CLICK_REACTION_MS);
        this.pendingDead.set(id, { node, key, x, y, tsMs, timer });
    }

    private settleDead(id: number): void {
        const pending = this.pendingDead.get(id);
        if (!pending) return;
        this.pendingDead.delete(id);
        const state = this.targets.get(pending.key);
        if (!state || state.deadEmitted) return;

        // Any DOM change, navigation, scroll or keystroke after the click means
        // the click did something.
        if (
            hasReactionAfter(this.domReactions, pending.tsMs, DEAD_CLICK_REACTION_MS) ||
            hasReactionAfter(this.softReactions, pending.tsMs, DEAD_CLICK_REACTION_MS)
        ) {
            return;
        }

        // Collapse double/triple-clicks: one gesture is one attempt.
        if (pending.tsMs - state.lastUnreactedTs > DEAD_CLICK_GESTURE_MS) {
            state.unreactedGestures++;
        }
        state.lastUnreactedTs = pending.tsMs;
        state.unreactedClicks++;
        if (!state.firstUnreacted) state.firstUnreacted = pending;
        if (state.unreactedGestures < DEAD_CLICK_MIN_OCCURRENCES) return;

        state.deadEmitted = true;
        this.emit('dead', {
            node: pending.node,
            x: state.firstUnreacted.x,
            y: state.firstUnreacted.y,
            tsMs: state.firstUnreacted.tsMs,
            clickCount: state.unreactedClicks,
            occurrences: state.unreactedGestures,
            durationMs: 0,
        });
    }
}

/** True when a reaction landed strictly after `afterTs`, within `windowMs`. */
function hasReactionAfter(sorted: number[], afterTs: number, windowMs: number): boolean {
    const idx = upperBound(sorted, afterTs);
    return idx < sorted.length && sorted[idx] <= afterTs + windowMs;
}

/** True when a reaction landed strictly after `fromTs` and at or before `toTs`. */
function hasReactionInRange(sorted: number[], fromTs: number, toTs: number): boolean {
    const idx = upperBound(sorted, fromTs);
    return idx < sorted.length && sorted[idx] <= toTs;
}

function upperBound(sorted: number[], target: number): number {
    let lo = 0;
    let hi = sorted.length;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (sorted[mid] <= target) lo = mid + 1;
        else hi = mid;
    }
    return lo;
}
